import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { workJournalAPI } from '../lib/api';
import {
  BookOpen, Plus, Loader2, Edit, Trash2, Lock, Calendar, Search,
} from 'lucide-react';

const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
};
const emptyDraft = () => ({ date: today(), title: '', content: '' });

export function WorkJournalPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await workJournalAPI.list();
      setEntries(res.entries || []);
    } catch (e) {
      toast.error(e.message || 'Error al cargar el diario');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => [e.title, e.content, e.date].filter(Boolean).some((t) => t.toLowerCase().includes(q)));
  }, [entries, search]);

  const openNew = () => { setEditing(null); setDraft(emptyDraft()); setDialog(true); };
  const openEdit = (e) => { setEditing(e); setDraft({ date: e.date, title: e.title || '', content: e.content || '' }); setDialog(true); };

  const handleSave = async () => {
    if (!draft.content.trim()) { toast.error('Escribe el contenido de la entrada'); return; }
    setSaving(true);
    try {
      if (editing) {
        await workJournalAPI.update(editing.id, draft);
        toast.success('Entrada actualizada');
      } else {
        await workJournalAPI.create(draft);
        toast.success('Entrada guardada');
      }
      setDialog(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al guardar');
    }
    setSaving(false);
  };

  const handleDelete = async (e) => {
    if (!window.confirm('¿Eliminar esta entrada? No se puede deshacer.')) return;
    setBusyId(e.id);
    try {
      await workJournalAPI.delete(e.id);
      toast.success('Entrada eliminada');
      await load();
    } catch (err) {
      toast.error(err.message || 'Error al eliminar');
    }
    setBusyId(null);
  };

  return (
    <div className="space-y-6" data-testid="work-journal-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-primary" /> Journal
          </h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> Tu diario de trabajo personal y privado. En qué trabajas, notas del día, bloqueos y avances.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-journal-entry-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva entrada
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar en mis entradas..." className="pl-9" data-testid="journal-search" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando...
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">{entries.length === 0 ? 'Aún no tienes entradas. Escribe la primera.' : 'No hay entradas que coincidan con la búsqueda.'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="journal-entries-list">
          {filtered.map((e) => (
            <motion.div key={e.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <Card data-testid={`journal-entry-${e.id}`} className="hover:shadow-sm transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="outline" className="gap-1 text-xs font-normal">
                          <Calendar className="w-3 h-3" /> {fmtDate(e.date)}
                        </Badge>
                        {e.title && <span className="font-semibold">{e.title}</span>}
                      </div>
                      <p className="text-sm whitespace-pre-wrap text-foreground/90">{e.content}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`edit-journal-${e.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(e)} disabled={busyId === e.id} data-testid={`delete-journal-${e.id}`}>
                        {busyId === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-red-500" />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg" data-testid="journal-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar entrada' : 'Nueva entrada'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha</Label>
                <Input type="date" value={draft.date} onChange={(ev) => setDraft({ ...draft, date: ev.target.value })} data-testid="journal-date" />
              </div>
              <div>
                <Label>Título <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                <Input value={draft.title} onChange={(ev) => setDraft({ ...draft, title: ev.target.value })} placeholder="Ej: Campaña rebajas" data-testid="journal-title" />
              </div>
            </div>
            <div>
              <Label>Contenido</Label>
              <Textarea rows={6} value={draft.content} onChange={(ev) => setDraft({ ...draft, content: ev.target.value })} placeholder="En qué estás trabajando, notas del día, bloqueos, avances..." data-testid="journal-content" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-journal-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? 'Guardar cambios' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default WorkJournalPage;
